// 3D view images for the PDF report (port of the HTML tool's capture pipeline): a cover image (iso, no dimensions,
// cropped to the content), a 3D view, a front view and a side view (a section, the elements being solid), taken
// from the off-screen report viewer (tc3dViewer), built from the same scene data as the interactive view. Each image
// composes the WebGL canvas with the SVG dimension overlay. Expensive (moves the camera, rasterises the overlay): run
// only on request, never on every recalculation.
import * as THREE from "three";
import type { Translation } from "../../../hooks/useTranslations";
import type { DimFilter, TC3DViewer, ViewName } from "./tc3dViewer";

// The 3D view's texts: view names and legibility warnings.
export type CaptureTexts = Translation["screwConnectorModule"]["calculation"]["view"];

export type LegibilityReport = {
  clipped: number;
  clippedTexts: string[];
  overlapsBefore: number;
  overlapsAfter: number;
};

export type ReportImages = {
  hero: string | null;
  iso: string | null;
  front: string | null;
  side: string | null;
  // per captured view ("iso", "front", "side*": '*' = with a dimension filter)
  legibility: Record<string, LegibilityReport>;
  // one line per view with clipped/overlapping dimension labels, and the HTML's warning sentence built from them
  warnings: string[];
  warningMessage: string | null;
};

export type OverlayAnalysis = {
  svg: SVGSVGElement;
  clipped: number;
  clippedAfter: number;
  clippedTexts: string[];
  before: number;
  after: number;
};

// A copy of the overlay sized in CSS pixels (the coordinates TC3DViewer.project uses): out of the page the SVG
// would not get the width/height 100% it is drawn with on screen.
function overlayCopy(viewer: TC3DViewer): SVGSVGElement {
  const c = viewer.overlay.cloneNode(true) as SVGSVGElement;
  c.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  c.setAttribute("width", String(viewer.container.clientWidth));
  c.setAttribute("height", String(viewer.container.clientHeight));
  return c;
}

// The dimension overlay as an image that can be drawn on a canvas, rasterised scale times its CSS size so its
// labels stay crisp at the capture's higher resolution.
export function svgOverlayToImage(
  viewer: TC3DViewer,
  analysis: OverlayAnalysis | null,
  scale = 1,
): Promise<HTMLImageElement> {
  const svgClone = analysis ? analysis.svg : overlayCopy(viewer);
  if (scale !== 1) {
    const width = viewer.container.clientWidth;
    const height = viewer.container.clientHeight;
    svgClone.setAttribute("width", String(width * scale));
    svgClone.setAttribute("height", String(height * scale));
    svgClone.setAttribute("viewBox", `0 0 ${width} ${height}`);
  }
  const svgStr = new XMLSerializer().serializeToString(svgClone);
  const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = svgUrl;
  });
}

// Legibility check of the dimension labels, on a copy of the overlay (sized as the viewer):
//  1) measures every text label (getBBox) and counts those CLIPPED by the image border;
//  2) counts the OVERLAPPING pairs; with declutter it moves them (vertically first, then horizontally) until they
//     separate, with a thin leader line from the original position.
export function analyseOverlayLabels(viewer: TC3DViewer, declutter: boolean): OverlayAnalysis {
  const W = viewer.container.clientWidth;
  const H = viewer.container.clientHeight;
  const PAD = 2;
  const svg = overlayCopy(viewer);
  const host = document.createElement("div");
  host.style.cssText = `position:absolute;left:-99999px;top:0;width:${W}px;height:${H}px;visibility:hidden`;
  host.appendChild(svg);
  document.body.appendChild(host);
  type Item = { t: SVGTextElement; x: number; y: number; w: number; h: number; cx0: number; cy0: number };
  const items: Item[] = [...svg.querySelectorAll("text")]
    .map((t) => {
      const bb = t.getBBox();
      return { t, x: bb.x, y: bb.y, w: bb.width, h: bb.height, cx0: bb.x + bb.width / 2, cy0: bb.y + bb.height / 2 };
    })
    .filter((it) => it.w > 0 && it.h > 0);
  const isClipped = (it: Item) => it.x < -0.5 || it.y < -0.5 || it.x + it.w > W + 0.5 || it.y + it.h > H + 0.5;
  const clippedItems = items.filter(isClipped);
  const clipped = clippedItems.length;
  const clippedTexts = clippedItems.map(
    (it) =>
      it.t.textContent +
      "@" +
      Math.round(it.x) +
      "," +
      Math.round(it.y) +
      " " +
      Math.round(it.w) +
      "x" +
      Math.round(it.h),
  );
  const ov = (a: Item, b: Item) =>
    a.x < b.x + b.w + PAD && b.x < a.x + a.w + PAD && a.y < b.y + b.h + PAD && b.y < a.y + a.h + PAD;
  const countOv = () => {
    let n = 0;
    for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) if (ov(items[i]!, items[j]!)) n++;
    return n;
  };
  const before = countOv();
  if (declutter && (before || clipped)) {
    // labels out of the image (b, t at the ends of long elements): brought inside, the leader line ties them back
    items.forEach((it) => {
      it.x = Math.min(Math.max(it.x, 0), Math.max(0, W - it.w));
      it.y = Math.min(Math.max(it.y, 0), Math.max(0, H - it.h));
    });
    const move = (it: Item, dx: number, dy: number) => {
      it.x = Math.min(Math.max(it.x + dx, 0), W - it.w);
      it.y = Math.min(Math.max(it.y + dy, 0), H - it.h);
    };
    for (let pass = 0; pass < 120; pass++) {
      let moved = false;
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i]!;
          const b = items[j]!;
          if (!ov(a, b)) continue;
          moved = true;
          if (pass < 60) {
            // vertical: move b above or below a, whichever is the smaller shift
            const down = a.y + a.h + PAD - b.y;
            const up = -(b.y + b.h + PAD - a.y);
            move(b, 0, Math.abs(down) <= Math.abs(up) ? down : up);
          } else {
            // horizontal (last resort)
            const right = a.x + a.w + PAD - b.x;
            const left = -(b.x + b.w + PAD - a.x);
            move(b, Math.abs(right) <= Math.abs(left) ? right : left, 0);
          }
        }
      }
      if (!moved) break;
    }
    items.forEach((it) => {
      const dx = it.x + it.w / 2 - it.cx0;
      const dy = it.y + it.h / 2 - it.cy0;
      if (Math.hypot(dx, dy) < 0.5) return;
      const bb0x = parseFloat(it.t.getAttribute("x") ?? "") || 0;
      const bb0y = parseFloat(it.t.getAttribute("y") ?? "") || 0;
      it.t.setAttribute("x", String(bb0x + dx));
      it.t.setAttribute("y", String(bb0y + dy));
      if (Math.hypot(dx, dy) > 5 && it.t.parentNode) {
        const ln = document.createElementNS("http://www.w3.org/2000/svg", "line");
        ln.setAttribute("x1", String(it.cx0));
        ln.setAttribute("y1", String(it.cy0));
        ln.setAttribute("x2", String(it.x + it.w / 2));
        ln.setAttribute("y2", String(it.y + it.h / 2));
        ln.setAttribute("stroke", "#9a9a9a");
        ln.setAttribute("stroke-width", "0.6");
        it.t.parentNode.insertBefore(ln, it.t);
      }
    });
  }
  const after = declutter ? countOv() : before;
  const clippedAfter = declutter ? items.filter(isClipped).length : clipped;
  host.remove();
  return { svg, clipped, clippedAfter, clippedTexts, before, after };
}

// Materials of every mesh of the group, without duplicates.
export function collectGroupMaterials(group: THREE.Object3D): THREE.Material[] {
  const mats = new Set<THREE.Material>();
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach((m) => mats.add(m));
  });
  return [...mats];
}

export type SnapshotOptions = {
  dims?: boolean; // false: no dimension overlay
  forceOpaque?: boolean; // timber at full opacity even if "Mostra quote" makes it semi-transparent on screen
  ortho?: boolean; // near-orthographic projection on the scene's sideFrame
  frame?: "group"; // frame the whole group instead of the screws
  frameFactor?: number;
  dimFilter?: DimFilter;
};

// Moves the viewer to the preset view (instantly), frames the connection for that view and captures the frame
// (canvas + dimension overlay) as a PNG data URL.
export async function captureViewerSnapshot(
  viewer: TC3DViewer,
  viewName: ViewName,
  opts: SnapshotOptions = {},
  legibility?: Record<string, LegibilityReport>,
): Promise<string> {
  const dims = opts.dims !== false;
  viewer.setView(viewName);
  // setView keeps the zoom/pivot of the previous view: re-centre on the screw group (the connection, not the whole
  // scene whose timber is longer than d1/d2 for scenic reasons) and fit the zoom to this view
  const sf = opts.ortho && viewer.sideFrame ? viewer.sideFrame : null;
  if (sf) {
    viewer.setOrtho(true);
    viewer.frameRectNow(sf.cx, sf.cy, sf.cz, sf.halfW, sf.halfH);
  } else if (opts.frame === "group") viewer.frameGroupNow(1.15);
  else viewer.frameProductNow(opts.frameFactor);
  let prevMatProps: { mat: THREE.Material; transparent: boolean; opacity: number }[] | null = null;
  if (opts.forceOpaque) {
    prevMatProps = collectGroupMaterials(viewer.group).map((mat) => {
      const prev = { mat, transparent: mat.transparent, opacity: mat.opacity };
      mat.transparent = false;
      mat.opacity = 1;
      return prev;
    });
  }
  try {
    if (opts.dimFilter) viewer.setDimFilter(opts.dimFilter);
    viewer.redraw();
    let analysis: OverlayAnalysis | null = null;
    if (dims) {
      // 1) zoom out step by step until no label is clipped by the image border
      const base = sf ? 1 : opts.frame === "group" ? null : opts.frameFactor || 3.2;
      const factors = base == null ? [null] : [base, base * 1.25, base * 1.6, base * 2.1, base * 2.8];
      for (const f of factors) {
        if (sf) {
          const k = f ?? 1;
          viewer.frameRectNow(sf.cx, sf.cy, sf.cz, sf.halfW * k, sf.halfH * k);
          viewer.redraw();
        } else if (f != null) {
          viewer.frameProductNow(f);
          viewer.redraw();
        }
        analysis = analyseOverlayLabels(viewer, false);
        if (analysis.clipped === 0) break;
      }
      // 2) separate the overlapping labels (with a leader line)
      analysis = analyseOverlayLabels(viewer, true);
      if (legibility) {
        legibility[viewName + (opts.dimFilter ? "*" : "")] = {
          clipped: analysis.clippedAfter,
          clippedTexts: analysis.clippedTexts,
          overlapsBefore: analysis.before,
          overlapsAfter: analysis.after,
        };
      }
    }
    const rendererCanvas = viewer.renderer.domElement;
    const svgImg = dims
      ? await svgOverlayToImage(viewer, analysis, rendererCanvas.width / viewer.container.clientWidth)
      : null;
    const out = document.createElement("canvas");
    out.width = rendererCanvas.width;
    out.height = rendererCanvas.height;
    const ctx = out.getContext("2d");
    if (ctx) {
      ctx.drawImage(rendererCanvas, 0, 0, out.width, out.height);
      if (svgImg) ctx.drawImage(svgImg, 0, 0, out.width, out.height);
    }
    return out.toDataURL("image/png");
  } finally {
    if (sf) viewer.setOrtho(false);
    if (opts.dimFilter) {
      viewer.setDimFilter(null);
      viewer.redraw();
    }
    if (prevMatProps) {
      prevMatProps.forEach((p) => {
        p.mat.transparent = p.transparent;
        p.mat.opacity = p.opacity;
      });
    }
  }
}

// Crops an image (white background) to its non-white content plus a proportional margin and brings it to the
// requested aspect ratio on white (the report cover: the elements with the screws, no empty space around).
export function autoCropToContent(dataUrl: string, aspect: number, pad: number, outW?: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width;
      c.height = img.height;
      const x = c.getContext("2d");
      if (!x) {
        resolve(dataUrl);
        return;
      }
      x.drawImage(img, 0, 0);
      const d = x.getImageData(0, 0, c.width, c.height).data;
      let x0 = c.width;
      let y0 = c.height;
      let x1 = -1;
      let y1 = -1;
      for (let y = 0; y < c.height; y++) {
        for (let xx = 0; xx < c.width; xx++) {
          const i = (y * c.width + xx) * 4;
          if (d[i + 3]! > 8 && (d[i]! < 245 || d[i + 1]! < 245 || d[i + 2]! < 245)) {
            if (xx < x0) x0 = xx;
            if (xx > x1) x1 = xx;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) {
        resolve(dataUrl);
        return;
      }
      let w = (x1 - x0 + 1) * (1 + 2 * pad);
      let h = (y1 - y0 + 1) * (1 + 2 * pad);
      if (w / h < aspect) w = h * aspect;
      else h = w / aspect;
      const cx = (x0 + x1) / 2;
      const cy = (y0 + y1) / 2;
      const o = document.createElement("canvas");
      o.width = outW || 640;
      o.height = Math.round(o.width / aspect);
      const ox = o.getContext("2d");
      if (!ox) {
        resolve(dataUrl);
        return;
      }
      ox.fillStyle = "#fff";
      ox.fillRect(0, 0, o.width, o.height);
      ox.drawImage(img, cx - w / 2, cy - h / 2, w, h, 0, 0, o.width, o.height);
      resolve(o.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// Fixed capture area, independent of the window: the report viewer sits off screen with no size and gets this one
// for the duration of the capture.
export const REPORT_CAPTURE_W = 1000;
export const REPORT_CAPTURE_H = 620;
// Rasterise the captures at twice their CSS size for print: at the PDF's full content width this is about 280 dpi.
const REPORT_IMAGE_PIXEL_RATIO = 2;
const REPORT_IMAGE_W = REPORT_CAPTURE_W * REPORT_IMAGE_PIXEL_RATIO;

// side view: ONLY the t1/t2 thicknesses (the others pile up on the same line)
const sideDimFilter: DimFilter = (c) => c.kind === "line" && !!c.opts && c.opts.only === "side";

// An off-screen container still needs a layout: ancestors hidden with display:none (a collapsed card) are shown
// for the duration of the capture - with no height and hidden, so the page does not move - and then restored.
function revealHiddenAncestors(el: HTMLElement): () => void {
  const restores: (() => void)[] = [];
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    if (getComputedStyle(a).display !== "none") continue;
    const prev = a.getAttribute("style");
    const target = a;
    target.style.setProperty("display", "block", "important");
    target.style.setProperty("height", "0", "important");
    target.style.setProperty("overflow", "hidden", "important");
    target.style.setProperty("visibility", "hidden", "important");
    restores.push(() => {
      if (prev == null) target.removeAttribute("style");
      else target.setAttribute("style", prev);
    });
  }
  return () => restores.reverse().forEach((r) => r());
}

export function legibilityWarningMessage(lines: string[], t: CaptureTexts): string {
  return t.capture.legibilityWarning(lines.join("; "));
}

const inFlight = new WeakMap<TC3DViewer, Promise<ReportImages>>();

// Captures the report images of the viewer's current scene. A call while a capture of the same viewer is running gets
// that capture's result.
export function captureReportImages(viewer: TC3DViewer, t: CaptureTexts): Promise<ReportImages> {
  const running = inFlight.get(viewer);
  if (running) return running;
  const p = doCaptureReportImages(viewer, t).finally(() => inFlight.delete(viewer));
  inFlight.set(viewer, p);
  return p;
}

async function doCaptureReportImages(v: TC3DViewer, t: CaptureTexts): Promise<ReportImages> {
  const images: ReportImages = {
    hero: null,
    iso: null,
    front: null,
    side: null,
    legibility: {},
    warnings: [],
    warningMessage: null,
  };
  const container = v.container;
  const restoreAncestors = revealHiddenAncestors(container);
  const prevInline = container.getAttribute("style");
  container.style.cssText =
    (prevInline || "") +
    `;position:fixed;left:-20000px;top:0;width:${REPORT_CAPTURE_W}px;height:${REPORT_CAPTURE_H}px;max-width:none`;
  v.resize();
  v.renderer.setPixelRatio(REPORT_IMAGE_PIXEL_RATIO);
  try {
    images.hero = await autoCropToContent(
      await captureViewerSnapshot(v, "iso", { dims: false, forceOpaque: true, frame: "group" }),
      4 / 3,
      0.06,
    );
    images.iso = await autoCropToContent(
      await captureViewerSnapshot(v, "iso", { forceOpaque: true }, images.legibility),
      1.5,
      0.04,
      REPORT_IMAGE_W,
    );
    images.front = await autoCropToContent(
      await captureViewerSnapshot(v, "front", { forceOpaque: true }, images.legibility),
      1.5,
      0.04,
      REPORT_IMAGE_W,
    );
    images.side = await autoCropToContent(
      await captureViewerSnapshot(
        v,
        "side",
        { dims: true, ortho: true, frameFactor: 1.5, dimFilter: sideDimFilter },
        images.legibility,
      ),
      1.5,
      0.05,
      REPORT_IMAGE_W,
    );
  } finally {
    if (prevInline == null) container.removeAttribute("style");
    else container.setAttribute("style", prevInline);
    restoreAncestors();
  }
  images.warnings = Object.entries(images.legibility)
    .filter(([, r]) => r.clipped > 0 || r.overlapsAfter > 0)
    .map(([k, r]) =>
      t.capture.legibilityLine(t.toolbar.views[k.replace("*", "") as ViewName], r.clipped, r.overlapsAfter),
    );
  images.warningMessage = images.warnings.length ? legibilityWarningMessage(images.warnings, t) : null;
  return images;
}
